// แก้ผลการเช็คชื่อละหมาดรายคน
//
// **หน้านี้ไม่ใช่งานประจำวัน** ระบบปิดรอบเองเมื่อหมดเวลารับแตะบัตร แล้วเหมาคนที่
// ไม่แตะบัตรเป็นขาดให้เลย ครูไม่ต้องมากดอะไรในวันปกติ
//
// หน้านี้มีไว้สำหรับข้อยกเว้นที่ระบบรู้เองไม่ได้: นักเรียนที่มาจริงแต่ลืมบัตร
// หรือป่วยนอนอยู่หอ ถ้าไม่มีทางแก้เลย เครื่องอ่านเสียหนึ่งรอบจะกลายเป็น
// "ขาดทั้งโรงเรียน" ที่ไม่มีใครแก้ได้
//
// ค่าเริ่มต้นจึงแสดงเฉพาะคนที่ขาด เพราะนั่นคือกลุ่มเดียวที่อาจต้องแก้
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';
const UPLOAD_BASE = API.replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);

const PRAYERS = [
  { key: 'fajr', label: 'ซุบฮิ' },
  { key: 'dhuhr', label: 'ซุฮ์รี' },
  { key: 'asr', label: 'อัสรี' },
  { key: 'maghrib', label: 'มัฆริบ' },
  { key: 'isha', label: 'อิชาอ์' },
];

// ต้องตรงกับ STATUS ใน server/models/prayerModel.js
const STATUS = { ABSENT: 0, PRESENT: 1, LATE: 2, NO_CARD: 3, EXCUSED: 4, SICK: 5 };

const STATUS_LABEL = {
  0: { text: 'ขาด', variant: 'danger' },
  1: { text: 'มา', variant: 'success' },
  2: { text: 'มาช้า', variant: 'warning' },
  3: { text: 'ลืมบัตร', variant: 'success' },
  4: { text: 'ได้รับยกเว้น', variant: 'secondary' },
  5: { text: 'ป่วยอยู่หอ', variant: 'info' },
};

// ปุ่มที่ครูกดได้ เรียงตามความถี่ที่ใช้จริง
// ไม่มีปุ่ม "มา" เฉยๆ เพราะการบอกว่ามาโดยไม่มีบัตรคือ "ลืมบัตร" ซึ่งตรงกว่า
// และทำให้แยกออกได้ทีหลังว่ามีคนลืมบัตรเยอะแค่ไหน
const ACTIONS = [
  { status: STATUS.NO_CARD, label: 'ลืมบัตร', hint: 'มาจริงแต่ไม่มีบัตร', variant: 'success', icon: 'ri-bank-card-line' },
  { status: STATUS.SICK, label: 'ป่วยอยู่หอ', hint: 'ตัดออกจากตัวหาร', variant: 'info', icon: 'ri-empathize-line' },
  { status: STATUS.EXCUSED, label: 'ได้รับยกเว้น', hint: 'ตัดออกจากตัวหาร', variant: 'secondary', icon: 'ri-shield-check-line' },
  { status: STATUS.LATE, label: 'มาช้า', hint: 'นับว่ามา', variant: 'warning', icon: 'ri-time-line' },
  { status: STATUS.ABSENT, label: 'ขาด', hint: 'กลับเป็นขาด', variant: 'outline-danger', icon: 'ri-close-line' },
];

const FILTERS = [
  { key: 'absent', label: 'ขาด' },
  { key: 'attended', label: 'มา' },
  { key: 'excused', label: 'ยกเว้น/ป่วย' },
  { key: 'pending', label: 'ยังไม่ลงผล' },
  { key: 'all', label: 'ทั้งหมด' },
];

const todayISO = () => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
};

const StudentAvatar = ({ photo, name, size = 44 }) => {
  const src = photoUrl(photo);
  return src ? (
    <img src={src} alt={name} width={size} height={size} className="rounded-circle flex-shrink-0" style={{ objectFit: 'cover' }} />
  ) : (
    <span
      className="rounded-circle bg-secondary bg-opacity-25 d-inline-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <i className="ri-user-line text-secondary"></i>
    </span>
  );
};

export default function PrayerCorrection() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];

  const [date, setDate] = useState(todayISO());
  const [prayer, setPrayer] = useState('maghrib');
  const [filter, setFilter] = useState('absent');
  const [keyword, setKeyword] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/roster`, {
        params: { date, prayer, status: filter, q: keyword || undefined },
      });
      setRows(data);
    } catch (error) {
      setRows([]);
      Swal.fire('อ่านรายชื่อไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setLoading(false);
    }
  }, [date, prayer, filter, keyword]);

  useEffect(() => { load(); }, [load]);

  // จัดกลุ่มตามอาคาร เพราะครูเวรรับผิดชอบเป็นรายอาคาร ไม่ใช่ทั้งโรงเรียน
  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      const key = row.dorm_name || 'ไม่ระบุอาคาร';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'th'));
  }, [rows]);

  const apply = async (stId, status) => {
    setSaving(true);
    try {
      await axiosInstance.post(`${API}/prayer/correct`, {
        date, prayer, entries: [{ st_id: stId, status }],
      });
      // อัปเดตในหน้าเลยโดยไม่โหลดใหม่ทั้งชุด — รายชื่ออาจเป็นร้อย
      // การรีเฟรชทุกครั้งที่กดจะทำให้ครูเลื่อนหาที่เดิมไม่เจอ
      setRows((prev) => (
        filter === 'all'
          ? prev.map((r) => (r.st_id === stId ? { ...r, status } : r))
          : prev.filter((r) => r.st_id !== stId)
      ));
    } catch (error) {
      Swal.fire('บันทึกไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">แก้ผลการละหมาด</h4>
          <div className="fs-xs text-secondary">
            ใช้เมื่อมีข้อยกเว้นเท่านั้น ไม่ต้องเข้ามาทุกวัน
          </div>
        </div>

        <Alert variant="light" className="fs-sm">
          ระบบปิดรอบเองเมื่อหมดเวลารับแตะบัตร แล้วลงผลให้ครบทุกคน
          หน้านี้มีไว้แก้กรณีที่ระบบรู้เองไม่ได้ เช่น นักเรียนมาจริงแต่ลืมบัตร หรือป่วยนอนอยู่หอ
        </Alert>

        <Card className="card-one mb-3">
          <Card.Body className="p-2 p-md-3">
            <Row className="g-2 align-items-end">
              <Col xs={6} md={3}>
                <Form.Label className="fs-xs text-secondary mb-1">วันที่</Form.Label>
                <Form.Control type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="fs-xs text-secondary mb-1">เวลาละหมาด</Form.Label>
                <Form.Select value={prayer} onChange={(e) => setPrayer(e.target.value)}>
                  {PRAYERS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fs-xs text-secondary mb-1">ค้นหา</Form.Label>
                <Form.Control
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="ชื่อ / เลขประจำตัว / อาคาร"
                />
              </Col>
            </Row>

            <div className="btn-group mt-2 flex-wrap" role="group">
              {FILTERS.map((f) => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={filter === f.key ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>

        <Card className="card-one">
          <Card.Header className="d-flex align-items-center gap-2">
            <Card.Title as="h6" className="mb-0 flex-fill">
              {rows.length} คน
              {rows.length >= 500 && <span className="fs-xs text-secondary ms-2">(แสดงได้สูงสุด 500 — ใช้ช่องค้นหาเพื่อแคบลง)</span>}
            </Card.Title>
            <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading} aria-label="รีเฟรช">
              {loading ? <Spinner size="sm" animation="border" /> : <i className="ri-refresh-line"></i>}
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : rows.length === 0 ? (
              <div className="text-center text-secondary py-4">
                <i className="ri-checkbox-circle-line d-block mb-2" style={{ fontSize: 40 }}></i>
                ไม่มีรายชื่อในเงื่อนไขนี้
              </div>
            ) : grouped.map(([dorm, students]) => (
              <div key={dorm}>
                <div className="px-3 py-2 bg-secondary bg-opacity-10 fw-semibold fs-sm">
                  {dorm} <span className="text-secondary fw-normal">({students.length} คน)</span>
                </div>
                <ul className="list-group list-group-flush">
                  {students.map((student) => {
                    const current = STATUS_LABEL[student.status];
                    return (
                      <li key={student.st_id} className="list-group-item py-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <StudentAvatar photo={student.st_photo} name={student.st_name} />
                          <div className="flex-fill min-width-0">
                            <div className="text-truncate">{student.st_name}</div>
                            <div className="fs-xs text-secondary">
                              {student.st_idnumber}
                              {student.scanned_at && ` · แตะบัตร ${String(student.scanned_at).slice(0, 5)}`}
                              {student.corrected_by && ` · แก้โดย ${student.corrected_by}`}
                            </div>
                          </div>
                          {current
                            ? <Badge bg={current.variant}>{current.text}</Badge>
                            : <Badge bg="light" text="dark">ยังไม่ลงผล</Badge>}
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {ACTIONS.filter((a) => a.status !== student.status).map((action) => (
                            <Button
                              key={action.status}
                              size="sm"
                              variant={action.variant.startsWith('outline') ? action.variant : `outline-${action.variant}`}
                              disabled={saving}
                              onClick={() => apply(student.st_id, action.status)}
                              title={action.hint}
                            >
                              <i className={`${action.icon} me-1`}></i>{action.label}
                            </Button>
                          ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </Card.Body>
        </Card>

        <Footer />
      </div>
    </>
  );
}
